import { useMutation } from '@apollo/client/react'
import { CREATE_SEND_MAIL_MUTATION } from 'api/symfony/graphql/mutations/createSendMail'
import { useCallback, useState } from 'react'

import { parseCreateSendMailResponse } from 'api/symfony/parsers/graphqlParsers'

import {
  CreateAttachmentInput,
  CreateSendMailInput,
  Mutation,
} from '@definitions/types/symfonyTypes'

export interface SendMailVariables {
  [key: string]: unknown
}

export default function useSendMail(): {
  sendMail: (
    subject: string,
    variables: SendMailVariables,
    template: string,
    attachments: CreateAttachmentInput[],
    sender?: string,
    data?: string
  ) => Promise<void>
  sendFormDataMail: <T extends Record<string, unknown>>(
    subject: string,
    formData: T,
    template: string,
    attachments: CreateAttachmentInput[],
    sender?: string,
    data?: string
  ) => Promise<void>
  isLoading: boolean
  result: boolean | null
} {
  const [createSendMailMutation, { loading: isLoading }] =
    useMutation<Mutation>(CREATE_SEND_MAIL_MUTATION)

  const [result, setResult] = useState<boolean | null>(null)

  const sendMail = useCallback(
    async (
      subject: string,
      variables: SendMailVariables,
      template: string,
      attachments: CreateAttachmentInput[],
      sender?: string,
      data?: string
    ) => {
      try {
        const input: CreateSendMailInput = {
          subject,
          variables,
          template,
          attachments,
        }

        if (sender) {
          input.sender = sender
        }
        if (data) {
          input.data = data
        }

        try {
          const { data: responseData } = await createSendMailMutation({
            variables: {
              input,
            },
          })

          if (
            responseData &&
            'createSendMail' in responseData &&
            responseData.createSendMail
          ) {
            const parsed = parseCreateSendMailResponse(
              responseData.createSendMail
            )
            setResult(parsed.success)
          }
        } catch (error) {
          console.error(error)
          setResult(false)
        }
      } catch (error) {
        console.error(error)
      }
    },
    [createSendMailMutation]
  )

  const sendFormDataMail = useCallback(
    async <T extends Record<string, unknown>>(
      subject: string,
      formData: T,
      template: string,
      attachments: CreateAttachmentInput[],
      sender?: string,
      data?: string
    ) => {
      await sendMail(subject, { formData }, template, attachments, sender, data)
    },
    [sendMail]
  )

  return { sendMail, sendFormDataMail, isLoading, result }
}
